/**
 * MyTasks - displays tasks assigned to the current user
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { MyTask } from '@services/api';

interface MyTasksProps {
  tasks: MyTask[];
  isLoading?: boolean;
}

const priorityColors: Record<string, string> = {
  urgent: 'bg-red-50 dark:bg-red-900/20',
  high: 'bg-orange-50 dark:bg-orange-900/20',
  medium: 'bg-yellow-50 dark:bg-yellow-900/20',
  low: 'bg-green-50 dark:bg-green-900/20',
  none: '',
};

// Column ID to i18n key + color mapping (labelKey pattern — no module-level t() calls)
const columnLabelKeys: Record<string, { labelKey: string; color: string }> = {
  todo: { labelKey: 'task.columnLabels.todo', color: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200' },
  open: { labelKey: 'task.columnLabels.open', color: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200' },
  backlog: { labelKey: 'task.columnLabels.backlog', color: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200' },
  in_progress: { labelKey: 'task.columnLabels.in_progress', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200' },
  in_review: { labelKey: 'task.columnLabels.in_review', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200' },
  review: { labelKey: 'task.columnLabels.review', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200' },
  done: { labelKey: 'task.columnLabels.done', color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' },
  completed: { labelKey: 'task.columnLabels.completed', color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' },
};

const completedColumnIds = new Set(['done', 'completed']);

export function MyTasks({ tasks, isLoading }: MyTasksProps) {
  const { t } = useTranslation();

  // Filter out completed tasks (backend already filters, but defense-in-depth)
  const activeTasks = tasks.filter(
    (task) => !completedColumnIds.has(task.columnId) && !('completedAt' in task && task.completedAt)
  );

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">{t('dashboard.myTasks')}</h3>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="animate-pulse p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg"
            >
              <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4 mb-2" />
              <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">{t('dashboard.myTasks')}</h3>
      {activeTasks.length === 0 ? (
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-neutral-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">{t('dashboard.noTasksAssigned')}</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {activeTasks.map((task) => {
            const priorityClass = priorityColors[task.priority ?? 'none'] ?? priorityColors.none;
            const columnInfo = columnLabelKeys[task.columnId];
            const columnLabel = columnInfo
              ? t(columnInfo.labelKey, { defaultValue: task.columnId })
              : task.columnId;
            const columnColor = columnInfo?.color ?? 'bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200';

            return (
              <li
                key={task.id}
                className={`p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg ${priorityClass} hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                      {task.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {task.taskKey && (
                        <span className="text-xs text-neutral-500 dark:text-neutral-400">
                          {task.taskKey}
                        </span>
                      )}
                      {task.projectName && (
                        <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                          {task.projectName}
                        </span>
                      )}
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0 ${columnColor}`}
                  >
                    {columnLabel}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    {task.dueDate && (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {t('dashboard.due', { date: new Date(task.dueDate).toLocaleDateString() })}
                      </p>
                    )}
                  </div>
                  {task.boardId && (
                    <Link
                      to={`/boards/${task.boardId}`}
                      className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {task.boardName || t('dashboard.openBoard')} &rarr;
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
